import { useState } from 'react';
import { toast } from 'react-toastify';
import { loadRazorpay } from '../utils/loadRazorpay';
import {
	proceedToCheckout,
	syncSubscription,
	confirmUpgradePayment,
	abandonCheckout,
} from '../services/subscription';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { clearCheckout } from '../store/reducers/checkoutSlice/checkoutSlice';
import { fetchUserInfo } from '../store/action/userInfo';

export const usePayment = () => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	// Checkout creates the subscription before the Razorpay window opens, so
	// closing the window without paying used to leave it behind and the app
	// then showed "Payment Pending" / "Complete Payment". Discard it and
	// refresh, so everything is as it was before the plan was picked. Awaited
	// by the callers so a quick second attempt doesn't trip over the old one.
	const discardUnpaidCheckout = async (razorpay_subscription_id) => {
		if (!razorpay_subscription_id) return;
		try {
			await abandonCheckout({ razorpay_subscription_id });
			await dispatch(fetchUserInfo());
		} catch (err) {
			console.error('Failed to discard the closed checkout:', err);
		}
	};

	const initiatePayment = async ({
		billingDetails,
		plan,
		paymentType,
		billingType,
		paymentMethod,
	}) => {
		try {
			setLoading(true);

			const selectedMethod = paymentMethod;
			const isRecurring = paymentType === 'recurring';
			const activeBilling = plan?.billing?.find((item) => item.period === billingType);

			if (!activeBilling) {
				toast.error('Invalid billing plan');
				return;
			}

			const payload = {
				billing: {
					company_name: billingDetails.name,
					phone: billingDetails.phone,
					address_line1: billingDetails.address,
					city: billingDetails.city,
					state: billingDetails.state,
					pincode: billingDetails.pincode,
					country: billingDetails.country,
					gstin_required: billingDetails.gst,
					gstin: billingDetails.gstin || null,
				},
				plan: {
					razorpay_plan_id: activeBilling?.razorpay_plan_id,
					billing_period: activeBilling?.period,
					amount: activeBilling?.amount_in_rupees,
					is_recurring: isRecurring,
					payment_method: selectedMethod,
				},
			};

			const response = await proceedToCheckout(payload);

			if (!response?.success) {
				toast.error(response?.message || 'Unable to proceed checkout');
				return;
			}

			const razorpayLoaded = await loadRazorpay();

			if (!razorpayLoaded) {
				toast.error('Razorpay SDK failed to load');
				return;
			}

			const subscription = response?.data?.subscription;
			const options = {
				key: subscription?.razorpay_key_id,
				subscription_id: subscription?.subscription_id,
				name: 'Pharmnex',
				description: `${subscription?.plan_name} - ${subscription?.billing_period}`,
				currency: subscription?.currency || 'INR',

				prefill: {
					name: billingDetails?.name,
					contact: billingDetails?.phone,
					email: subscription?.customer?.email || '',
					// The radio on the billing form now sets the STARTING method
					// rather than the only one — see the method map below.
					...(selectedMethod ? { method: selectedMethod } : {}),
				},

				// Both card and UPI stay open.
				//
				// The billing form's Card/UPI radio used to be enforced here, so a
				// customer who picked UPI and then wanted to pay by card had to
				// close Razorpay, go back, change the radio and start again — by
				// which point a subscription row already existed for the abandoned
				// attempt. The radio is now a preference expressed through `prefill`.
				//
				// netbanking/wallet/emi/paylater stay explicitly false: an omitted
				// method falls back to whatever the Razorpay account has enabled,
				// and netbanking authorises an eMandate, which Razorpay then refuses
				// to change the plan on — breaking scheduled downgrades later.
				method: {
					upi: true,
					card: true,
					netbanking: false,
					wallet: false,
					emi: false,
					paylater: false,
				},

				// `preferences.show_default_blocks: false` used to be set here with
				// custom upi/card blocks. Razorpay lists a customer's SAVED CARDS in
				// its default blocks, so suppressing them meant the user had to
				// re-enter their card on every payment — and Razorpay issued a brand
				// new token each time, which surfaced as a duplicate entry under
				// Manage Billing.
				//
				// The `method` map above already narrows checkout to card and UPI,
				// so the custom blocks were only relabelling what `method` had
				// already limited. Leaving the default blocks in
				// place lets an existing saved card be selected and reused, and a new
				// card is tokenised only when the user actually enters a different
				// one.
				config: {
					display: {
						preferences: { show_default_blocks: true },
					},
				},

				...(isRecurring && { recurring: 1 }),

				theme: { color: '#3B58FF' },

				handler: async (paymentResponse) => {
					dispatch(clearCheckout());

					// Activation used to depend entirely on Razorpay's webhook, which
					// lands seconds-to-minutes after the user finishes paying. Fetching
					// user info straight away therefore returned the OLD plan (or
					// "Payment Pending"), and the user had to wait and refresh.
					//
					// Confirm the payment against Razorpay first so the backend applies
					// the new status synchronously; only then refresh Redux.
					try {
						await syncSubscription({
							razorpay_subscription_id:
								paymentResponse?.razorpay_subscription_id || subscription?.subscription_id,
						});
					} catch (err) {
						// Non-fatal: the webhook remains the backstop, and
						// /payment-success polls for the settled status.
						console.error('Failed to confirm subscription after payment:', err);
					}

					// openSubscriptionCheckout's success path does a full page
					// reload, which incidentally refreshes the app's Redux
					// userInfo/subscription state. This path navigates via
					// React Router instead (no reload), so without this the
					// rest of the app (sidebar, plan gates, etc.) would keep
					// showing pre-payment subscription state until the user
					// manually refreshed. Await it before navigating so
					// /payment-success and everything after it sees the new plan.
					try {
						await dispatch(fetchUserInfo());
					} catch (err) {
						console.error('Failed to refresh user info after payment:', err);
					}
					navigate('/payment-success', { replace: true });
				},

				modal: {
					ondismiss: async () => {
						toast.error('Payment cancelled');
						await discardUnpaidCheckout(subscription?.subscription_id);
					},
				},
			};

			const razorpay = new window.Razorpay(options);

			razorpay.on('payment.failed', (response) => {
				toast.error(response?.error?.description || 'Payment failed');
			});

			razorpay.open();
		} catch (error) {
			console.error(error);
			toast.error(error?.response?.data?.message || 'Payment initialization failed');
		} finally {
			setLoading(false);
		}
	};

	/**
	 * @param paymentMethod which method to PREFER, not the only one allowed.
	 *   Callers pass the method the customer used originally so Razorpay can
	 *   surface it first; they are no longer restricted to it. Pass null to
	 *   express no preference.
	 * @param onSuccess called once payment is confirmed and Redux userInfo has
	 *   been refetched — i.e. after the Razorpay modal actually closes with a
	 *   result, not when this function returns (`razorpay.open()` returns
	 *   immediately; the modal is async). Callers that keep their own copy of
	 *   subscription state outside Redux (the settings page's local
	 *   `subscriptionDetails`) use this to refetch it, which is the thing that
	 *   was missing: nothing refreshed that page after an upgrade/downgrade
	 *   requiring a fresh mandate, only after ones that didn't.
	 */
	const openSubscriptionCheckout = async ({
		subscription,
		billingDetails,
		paymentMethod = null,
		onProcessing,
		onSuccess,
		// Called when the customer closes the payment window or the payment is
		// declined — i.e. the plan did NOT change. Lets the caller undo any
		// optimistic "done" state it showed before opening checkout.
		onDismiss,
		onVerificationError,
	}) => {
		try {
			setLoading(true);

			const razorpayLoaded = await loadRazorpay();

			if (!razorpayLoaded) {
				toast.error('Razorpay SDK failed to load');
				return;
			}

			// A non-recurring upgrade has no mandate to reprice, so it is
			// collected as a one-time Invoice/Order payment instead of a
			// subscription — see the non-recurring branch of upgradeSubscription
			// on the backend. Razorpay Checkout takes `order_id` instead of
			// `subscription_id` for that case, and confirmation has to go
			// through confirmUpgradePayment (signature-verified) rather than
			// syncSubscription, since there is no subscription-side webhook for
			// an order payment to sync from.
			const isOrderBased = Boolean(subscription.order_id);

			const options = {
				key: subscription.razorpay_key_id,
				...(isOrderBased
					? { order_id: subscription.order_id }
					: { subscription_id: subscription.subscription_id }),
				name: 'Pharmnex',
				description: `${subscription.plan_name} - ${subscription.billing_period}`,
				currency: subscription.currency || 'INR',

				prefill: {
					name: billingDetails?.company_name,
					contact: billingDetails?.phone,
					email: subscription.customer?.email,
					// A preference, not a restriction: Razorpay opens on this method
					// while still offering the other. Spread in only when we have one,
					// because `method: undefined` is not the same as absent here.
					...(paymentMethod ? { method: paymentMethod } : {}),
				},

				// BOTH card and UPI stay open so the customer can choose inside
				// Razorpay's checkout.
				//
				// This used to enable only the single method they happened to use
				// when they first subscribed (`payment_details.method`), which meant
				// someone who signed up on UPI could never pay an upgrade by card
				// without abandoning the flow entirely. Nothing about the original
				// mandate requires this payment to use the same method.
				//
				// netbanking/wallet/emi/paylater stay off deliberately, and this is
				// NOT cosmetic: netbanking is how an eMandate subscription gets
				// authorised, and Razorpay refuses to change the plan on one
				// ("subscriptions cannot be updated when payment mode is emandate"),
				// which is exactly what scheduled downgrades depend on. Leaving them
				// out of the map is not enough — an omitted method falls back to
				// whatever the Razorpay account has enabled, so they must be false.
				method: {
					upi: true,
					card: true,
					netbanking: false,
					wallet: false,
					emi: false,
					paylater: false,
				},

				// `recurring: 1` tells Checkout to expect a subscription_id and is
				// meaningless (Razorpay ignores it) for an order-based payment.
				...(isOrderBased ? {} : { recurring: 1 }),

				theme: {
					color: '#3B58FF',
				},

				handler: async (paymentResponse) => {
					// Notify caller immediately that Razorpay checkout completed,
					// so it can transition to a dedicated processing/loading state
					// and dismiss the upgrade modal.
					onProcessing?.();
					// Same race as the checkout path: reloading immediately would
					// re-read a subscription that hasn't been activated yet, so the
					// page would come back showing the previous plan. Confirm the
					// payment first, then reload.
					try {
						if (isOrderBased) {
							// No subscription-side webhook exists for an order payment —
							// this call is what actually applies the plan change, not
							// just a display refresh (see confirmUpgradePayment).
							await confirmUpgradePayment({
								// Falls back to the order_id WE gave Checkout when we
								// opened it (same fallback pattern the subscription_id
								// branch below already uses). Razorpay's handler callback
								// is documented to always echo razorpay_order_id back for
								// an order-based payment, but on redirect-based methods
								// (UPI intent / netbanking on mobile — where Checkout has
								// to bounce the page out to a bank/UPI app and back) it
								// has been observed missing from the reconstructed
								// callback payload, which left this call short a required
								// field and rejected the whole upgrade with
								// "razorpay_order_id ... required" even though the
								// payment itself succeeded. Safe to fall back: it's the
								// exact order this session was opened with, and the
								// backend independently re-verifies the signature AND
								// cross-checks it against the invoice's real order_id
								// before applying anything (see confirmUpgradePayment in
								// services.js) — a wrong id here fails closed, it can't
								// be exploited.
								razorpay_order_id: paymentResponse?.razorpay_order_id || subscription.order_id,
								razorpay_payment_id: paymentResponse?.razorpay_payment_id,
								razorpay_signature: paymentResponse?.razorpay_signature,
								// Razorpay signs this callback using a DIFFERENT formula
								// (invoice_id|receipt|status|payment_id) than the standard
								// order formula (order_id|payment_id) when the order being
								// paid was auto-created via the Invoices API — which is
								// exactly this non-recurring-upgrade flow — even though
								// Checkout was opened with order_id. Undocumented, not
								// covered by Razorpay's own SDK helper (see
								// https://github.com/razorpay/razorpay-node/issues/450).
								// Forwarding these two extra fields (present on the
								// callback whenever this formula applies, undefined
								// otherwise) lets the backend try both and accept whichever
								// one Razorpay actually used, instead of only ever checking
								// the formula that was never guaranteed to match here.
								razorpay_invoice_receipt: paymentResponse?.razorpay_invoice_receipt,
								razorpay_invoice_status: paymentResponse?.razorpay_invoice_status,
								invoice_id: subscription.invoice_id,
							});
						} else {
							await syncSubscription({
								razorpay_subscription_id:
									paymentResponse?.razorpay_subscription_id || subscription.subscription_id,
							});
						}
					} catch (err) {
						console.error('Failed to confirm subscription after upgrade payment:', err);
						toast.error(
							err?.response?.data?.message ||
								'Payment received, but confirming the upgrade failed. Please refresh — support has the payment on record.',
						);
						onVerificationError?.(err);
						return;
					}

					// Was window.location.reload(). Refetching keeps this consistent
					// with the initiatePayment path above and stops the reload from
					// discarding any toast raised by the caller.
					try {
						await dispatch(fetchUserInfo());
					} catch (err) {
						console.error('Failed to refresh user info after upgrade payment:', err);
					}

					onSuccess?.();
				},

				// This flow had neither handler, unlike initiatePayment above.
				// Closing the Razorpay window — or having the payment declined —
				// left the upgrade/downgrade silently unfinished: no message, and
				// the pricing modal had already reported success. The customer had
				// no way to tell the change hadn't happened.
				modal: {
					ondismiss: async () => {
						toast.error('Payment cancelled — your plan has not been changed.');
						// An order-based payment has no subscription to discard; its
						// invoice is reused if the customer tries again.
						if (!isOrderBased) await discardUnpaidCheckout(subscription.subscription_id);
						onDismiss?.();
					},
				},
			};

			const razorpay = new window.Razorpay(options);

			// Razorpay also POSTs its own `payment.failed` webhook for this, which
			// is what triggers the failure email server-side. Dismissing the modal
			// without attempting payment raises NO Razorpay event at all, so that
			// case is client-side only by nature — hence the ondismiss above.
			razorpay.on('payment.failed', (response) => {
				toast.error(
					response?.error?.description || 'Payment failed — your plan has not been changed.',
				);
				onDismiss?.();
			});

			razorpay.open();
		} catch (error) {
			console.error('Failed to open Razorpay subscription checkout:', error);
			toast.error('Payment window failed to open. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return {
		initiatePayment,
		openSubscriptionCheckout,
		loading,
	};
};
